// Hook: useMessageActions
//
// Thin orchestration layer between the hover action bar / reaction
// chips and the `matrix.ts` wrapper. Components stay presentational;
// every side effect is named, toggled, and error-reported here.
//
// The hook keeps no optimistic copy of reactions or edits. The timeline
// subscription owns the source of truth — see #1294. A parallel cache
// here would duplicate state and start drifting the moment the
// homeserver reshuffles aggregation.

import { useCallback } from "react";

import { useMatrix } from "~/context/MatrixContext";
import {
  editText as matrixEditText,
  redactEvent as matrixRedactEvent,
  removeReaction as matrixRemoveReaction,
  sendReaction as matrixSendReaction,
  type ReactionSummary,
} from "~/lib/matrix";

export interface UseMessageActionsResult {
  /**
   * Toggle a reaction by emoji. If the user already reacted with this
   * emoji (`reactedByMe`), redacts their reaction; otherwise sends a
   * fresh one. The aggregated `ReactionSummary` list is the single
   * source of truth for "did I already react?" — the hook never guesses.
   *
   * Rejects with an explicit error when `ready` is false; callers are
   * expected to gate their UI on `ready` rather than relying on a
   * silent no-op that would leave `InlineEdit` / delete dialogs
   * closing as if the action had succeeded.
   */
  toggleReaction: (
    targetEventId: string,
    emoji: string,
    reactions: ReactionSummary[],
  ) => Promise<void>;
  /**
   * Send an edit for the target event. Empty bodies are rejected at the
   * wrapper layer, so the caller should disable Save on whitespace-only
   * input rather than relying on this hook to swallow the error.
   */
  editMessage: (targetEventId: string, newBody: string) => Promise<void>;
  /** Redact (delete) a message. `reason` is written to the audit log. */
  deleteMessage: (eventId: string, reason?: string) => Promise<void>;
  /** True when a Matrix session + room are available. Use this to
   *  disable action buttons before login completes. */
  ready: boolean;
}

/**
 * Bound to a specific room. Callers pass the `roomId` of the message
 * being acted on (typically `message.roomId` from the row). This is
 * the same prop-as-source-of-truth invariant that `EmbeddableChat`'s
 * `mode='room'` relies on — `MatrixContext.activeRoomId` is null in
 * an embed that never mounts the sidebar, so a hook reading from it
 * would render every action disabled.
 *
 * When the session isn't ready (no client, no roomId), each verb
 * rejects with a named error rather than silently no-op'ing — a
 * silent no-op would let `InlineEdit` close and a `DeleteConfirm`
 * dialog dismiss as if the action had succeeded, which is exactly
 * the kind of failure mode the UI gate is supposed to prevent.
 */
export function useMessageActions(roomId: string | null): UseMessageActionsResult {
  const { client } = useMatrix();
  const ready = client !== null && roomId !== null;

  const toggleReaction = useCallback(
    async (targetEventId: string, emoji: string, reactions: ReactionSummary[]) => {
      if (!client || !roomId) {
        throw new Error("toggleReaction: Matrix session not ready");
      }
      const existing = reactions.find((r) => r.emoji === emoji && r.reactedByMe);
      if (existing?.myReactionId) {
        await matrixRemoveReaction(client, roomId, existing.myReactionId);
        return;
      }
      await matrixSendReaction(client, roomId, targetEventId, emoji);
    },
    [client, roomId],
  );

  const editMessage = useCallback(
    async (targetEventId: string, newBody: string) => {
      if (!client || !roomId) {
        throw new Error("editMessage: Matrix session not ready");
      }
      await matrixEditText(client, roomId, targetEventId, newBody);
    },
    [client, roomId],
  );

  const deleteMessage = useCallback(
    async (eventId: string, reason = "deleted by user") => {
      if (!client || !roomId) {
        throw new Error("deleteMessage: Matrix session not ready");
      }
      await matrixRedactEvent(client, roomId, eventId, reason);
    },
    [client, roomId],
  );

  return { toggleReaction, editMessage, deleteMessage, ready };
}
